import RequestToken from '../../../../tokens/RequestToken';
import {CommandRequester} from '../../../../CommandRequester';
import {Event} from '../../../../events/Event';
import RequestAPI from '../../../RequestAPI';
import {HotWordEvents, StreamTypes, CommandTypes} from '@jibo/command-protocol';

export enum HotWords {
    "HEY_JIBO" = 'hey jibo',
}

/**
 * @class HotWordToken
 * @extends RequestToken
 * @hideconstructor
 */
export class HotWordToken extends RequestToken<JIBO.v1.HotWordRequest> {

    /**
     * Heard "Hey Jibo"
     * @name HotWordToken#hotWordHeard
     * @type {Event}
     */
    hotWordHeard:Event<JIBO.v1.HotWordHeardEvent>;

    /**
     * Result of what Jibo head is available.
     * @name HotWordToken#listenResult
     * @type {Event<string>}
     */
    listenResult:Event<string>;

    /** @private */
    constructor(owner:CommandRequester, protocol: JIBO.v1.HotWordRequest) {
        super(owner, protocol);
        this.hotWordHeard = new Event('Heard hot word');
        this.listenResult = new Event('Listen complete');
    }

    /**
     * @private
     */
    handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    handleEvent(event:JIBO.v1.EventMessage) {
        //handle response
        const message = event.EventBody;
        switch (message.Event) {
            case HotWordEvents.HotWordHeard:
                this.hotWordHeard.emit(message);
                break;
        }
    }
}

/**
 * Internal HotWord class
 * @class CommandRequester.listen.subscribe.HotWord
 * @intdocs
 */
export class HotWord extends RequestAPI<JIBO.v1.HotWordRequest, HotWordToken>{

    constructor(requester:CommandRequester) {
        super(requester);
    }

    /**
     * Generate HotWord Protocol
     * @method CommandRequester.listen.subscribe.HotWord#generateProtocol
     * @param {boolean} [listen=false] - Whether to listen for additional speech input after "Hey Jibo" is heard.
     * @returns {HotWordRequest}
     * @intdocs
     */
    static generateProtocol(listen = false, hotWord = HotWords.HEY_JIBO): JIBO.v1.HotWordRequest {
        return {
            Type: CommandTypes.Subscribe,
            StreamType: StreamTypes.HotWord,
            StreamFilter: {},
            Listen: listen
        };
    }

    /**
     * Create HotWordToken from HotWordRequest protocol.
     * @method CommandRequester.listen.subscribe.HotWord#generateToken
     * @param {HotWordRequest} protocol - HotWordRequest protocol to generate a HotWordToken from.
     * @param {boolean} [andSend=false] - `true` if the generated HotWordToken should also be sent as a request.
     * @return {HotWordToken}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.HotWordRequest, andSend=false): HotWordToken {
        const token = new HotWordToken(this.owner, protocol);
        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send HotWordToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.listen.subscribe.HotWord#sendToken
     * @param {HotWordToken} token - HotWordToken to send.
     * @intdocs
     */
    public sendToken(token: HotWordToken): void {
        this.owner.sendToken(token);
    }
}
